import { useState } from 'react'
import type { PipelineStage } from '../../types'
import { CURRENCIES, DEFAULT_CURRENCY, STAGE_LABELS } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { parseCurrencyInput } from '../../utils/currency'
import { Button, Input, Select, Modal } from '../ui'
import { toast } from '../ui/Toast'

interface AddDealModalProps {
  isOpen: boolean
  onClose: () => void
  initialStage: PipelineStage
}

export default function AddDealModal({
  isOpen,
  onClose,
  initialStage,
}: AddDealModalProps) {
  const { companies, addDeal } = useDataStore()
  
  const [title, setTitle] = useState('')
  const [value, setValue] = useState('')
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY)
  const [companyId, setCompanyId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setTitle('')
    setValue('')
    setCurrency(DEFAULT_CURRENCY)
    setCompanyId('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast('Title is required', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      await addDeal({
        title: title.trim(),
        value: parseCurrencyInput(value),
        currency,
        companyId: companyId || '',
        stage: initialStage,
        status: 'active',
      })
      toast('Deal created', 'success')
      handleClose()
    } catch {
      toast('Failed to create deal', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const currencyOptions = CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }))

  const companyOptions = [
    { value: '', label: 'No company' },
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New deal"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create deal'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Website Redesign"
          autoFocus
        />
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Value"
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0.00"
          />
          <Select
            label="Currency"
            options={currencyOptions}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          />
        </div>

        <Select
          label="Company (optional)"
          options={companyOptions}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        />

        <p className="text-xs text-text-secondary">
          Deal will be added to: {STAGE_LABELS[initialStage]}
        </p>
      </div>
    </Modal>
  )
}
